import * as React                      from 'react';

import Drawer                          from '@mui/material/Drawer';

import { LoupeVector                 } from 'src/components/vector/loupe';
import { DefectOKVector              } from 'src/components/vector/defect-ok';
import { EmptyCaseLoupeVector        } from 'src/components/vector/empty-case/loupe';

import { Checkbox                    } from 'src/components/common/checkbox';
import { IconButton                  } from 'src/components/common/icon-button';
import { EmptyCase                   } from 'src/components/common/empty-case';
import { Typography                  } from 'src/components/common/typography';
import { InputTextbox                } from 'src/components/common/input/input-textbox';
import { Grow                        } from 'src/components/common/flex/grow';

import { Container,
         HeaderContainer,
         SearchContainer,
         ReferItemsContainer,
         ReferItemContainer,
         ReferItemCheckbox,
         ReferItem,
         ReferItemLabel,
         ReferItemSubLabel,
         CloseOutlineVectorBig,
         ReferMultiSelectorVariants,
         refermultiselector_variants } from 'src/components/features/common/refer-multi-selector/ui';

import Constants                       from 'src/services/constants';



export type ReferMultiSelectorItem = {
  id: string;
  label: string;
  sublabel?: string;
}

type Props = {
  variant: ReferMultiSelectorVariants;
  referItemsList: ReferMultiSelectorItem[];
  isOpened: boolean;
  isSortByLabel?: boolean;
  isSearchable?: boolean;
  label: string;
  selectedIds: string[];
  onClose: () => void;
  onSelect: (referItems: ReferMultiSelectorItem[]) => void;
}
export const ReferMultiSelector: React.FC<Props> = ({
  variant,
  referItemsList,
  isOpened,
  isSortByLabel = false,
  isSearchable = false,
  label,
  selectedIds,
  onClose,
  onSelect,
}) => {
  const lookAndFeel = refermultiselector_variants.get(variant)!;

  const [searchPhrase, setSearchPhrase] = React.useState<string>('');
  const [listData, setListData] = React.useState<ReferMultiSelectorItem[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);

  const fullHeight = isSearchable || (referItemsList.length >= Constants.REFER_FULL_HEIGHT_COUNT);

  React.useEffect(() => {
    if (isOpened)
    {
      setSelected([...selectedIds]);
    }
  }, [isOpened, selectedIds]);

  React.useEffect(() => {
    if (referItemsList.length > 0)
    {
      setListData(
        [
          ...(isSortByLabel ? referItemsList.sort((a, b) => (a.label > b.label ? 1 : -1)) : referItemsList)
        ]
      );
    }
  }, [referItemsList, isSortByLabel]);

  React.useEffect(() => {
    const lcSearch = searchPhrase.toLowerCase();

    if (referItemsList.length > 0)
    {
      const workItems = [
        ...(isSortByLabel ? referItemsList.sort((a, b) => (a.label > b.label ? 1 : -1)) : referItemsList)
      ];
      
      if (searchPhrase.length > 0)
      {
        setListData(
          workItems
            .map((item) => {
              const lcLabel = item.label.toLowerCase();
              const lcSubLabel = item.sublabel?.toLowerCase() ?? '';
              const isLabelIncludes = lcLabel.includes(lcSearch);
              const isSubLabelIncludes = lcSubLabel.includes(lcSearch);
              if (isLabelIncludes || isSubLabelIncludes)
              {
                let label = item.label;
                let sublabel = item.sublabel;

                if (isLabelIncludes)
                {
                  const searchIndex = lcLabel.indexOf(lcSearch);
                  const startStr = item.label.substring(0, searchIndex);
                  const middleStr = item.label.substring(searchIndex, searchIndex + searchPhrase.length);
                  const endStr = item.label.substring(searchIndex + searchPhrase.length);

                  label = `${startStr}<span>${middleStr}</span>${endStr}`;
                }

                if (isSubLabelIncludes)
                {
                  const searchIndex = lcSubLabel!.indexOf(lcSearch);
                  const startStr = item.sublabel!.substring(0, searchIndex);
                  const middleStr = item.sublabel!.substring(searchIndex, searchIndex + searchPhrase.length);
                  const endStr = item.sublabel!.substring(searchIndex + searchPhrase.length);

                  sublabel = `${startStr}<span>${middleStr}</span>${endStr}`;
                }

                return ({
                  id: item.id,
                  label,
                  sublabel,
                })
              }
              
              return null;
            })
            .filter((item) => item !== null) as ReferMultiSelectorItem[]
        );
      }
      else
      {
        setListData(workItems);
      }
    }
  }, [searchPhrase, referItemsList, isSortByLabel]);

  const onToggleItemSelectionHandler = (referItem: ReferMultiSelectorItem) => {
    setSelected(
      (
        selected.includes(referItem.id) ?
          referItemsList.filter((item) => selected.includes(item.id) && item.id !== referItem.id)
          :
          referItemsList.filter((item) => selected.includes(item.id) || item.id === referItem.id)
      ).map((item) => item.id)
    );
    setSearchPhrase('');
  }

  const onCloseHandler = () => {
    setSearchPhrase('');
    onClose();
  }

  const onConfirmHandler = () => {
    onSelect(referItemsList.filter((item) => selected.includes(item.id)));
    onCloseHandler();
  }

  return (
    <Drawer
      anchor = 'bottom'
      open = { isOpened }
      onClose = { onCloseHandler }
      PaperProps={{ style: { borderRadius: fullHeight ? 0 : '15px 15px 0 0' } }}
    >
      <Container lookAndFeel = { lookAndFeel } fullHeight = { fullHeight }>
        <HeaderContainer lookAndFeel = { lookAndFeel }>
          <IconButton
            variant = { lookAndFeel.headerIconButtonVariant }
            isTapAllowed
            onTap = { onConfirmHandler }
          >
            <DefectOKVector />
          </IconButton>
          <Typography variant = { lookAndFeel.headerTypographyVariant }>
            { label }
          </Typography>
          <Grow size = { 2 } />
          <IconButton
            variant = { lookAndFeel.headerIconButtonVariant }
            isTapAllowed
            onTap = { onCloseHandler }
          >
            <CloseOutlineVectorBig />
          </IconButton>
        </HeaderContainer>
        {fullHeight && (
          <SearchContainer lookAndFeel = { lookAndFeel }>
            <InputTextbox
              variant = { lookAndFeel.searchInputVariant }
              iconPrefix = { <LoupeVector /> }
              value = { searchPhrase }
              onChange = { setSearchPhrase }
            />
          </SearchContainer>
        )}
        {referItemsList.length > 0 && listData.length === 0 && (
          <ReferItemsContainer lookAndFeel = { lookAndFeel }>
            <EmptyCase
              variant = { lookAndFeel.referEmptyCaseVariant }
              icon = { <EmptyCaseLoupeVector /> }
              header = 'Ничего не найдено'
              message = 'Попробуйте изменить поисковый запрос'
            />
          </ReferItemsContainer>
        )}
        {listData.length > 0 && (
          <ReferItemsContainer lookAndFeel = { lookAndFeel }>
            {listData.map((referItem) => (
              <ReferItemContainer
                key = { referItem.id }
                lookAndFeel = { lookAndFeel }
                onClick = { () => onToggleItemSelectionHandler(referItem) }
              >
                <ReferItemCheckbox lookAndFeel = { lookAndFeel }>
                  <Checkbox
                    variant = { lookAndFeel.referItemCheckboxVariant }
                    value = { selected.includes(referItem.id) }
                    onChange = { () => onToggleItemSelectionHandler(referItem) }
                  />
                </ReferItemCheckbox>
                <ReferItem
                  lookAndFeel = { lookAndFeel }
                  isSelected = { selected.includes(referItem.id) }
                  withSubLabel = { referItem.sublabel !== undefined }
                >
                  <ReferItemLabel
                    lookAndFeel = { lookAndFeel }
                    dangerouslySetInnerHTML = { { __html: referItem.label } }
                  />
                  {referItem.sublabel !== undefined && (
                    <ReferItemSubLabel
                      lookAndFeel = { lookAndFeel }
                      dangerouslySetInnerHTML = { { __html: referItem.sublabel } }
                    />
                  )}
                </ReferItem>
              </ReferItemContainer>
            ))}
          </ReferItemsContainer>
        )}
      </Container>
    </Drawer>
  );
};
